/**
 * Session management using sessionStorage (ephemeral per browser session)
 *
 * Sessions are NOT persisted across browser sessions/page loads to:
 * 1. Ensure privacy - different users on shared devices get fresh sessions
 * 2. Avoid sending old conversation context to AI
 * 3. Each page load starts fresh while allowing multi-turn within the session
 *
 * Includes fallback to in-memory storage when sessionStorage is unavailable
 * (private browsing, storage quota exceeded, etc.)
 */

import { createLogger } from './logger';

const log = createLogger('Session');

const SESSION_PREFIX = 'pms_session_';
const API_KEY_STORAGE_KEY = 'pms_api_key';

// In-memory fallback storage for when sessionStorage is unavailable
const memoryStorage = new Map<string, string>();
let useMemoryFallback = false;

/**
 * Check if sessionStorage is available
 */
function isSessionStorageAvailable(): boolean {
  if (useMemoryFallback) return false;

  try {
    const testKey = '__pms_test__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    return true;
  } catch {
    log.warn('sessionStorage unavailable, using in-memory fallback');
    useMemoryFallback = true;
    return false;
  }
}

/**
 * Get item from storage (sessionStorage or memory fallback)
 */
function getStorageItem(key: string): string | null {
  if (isSessionStorageAvailable()) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      log.warn('Failed to read from sessionStorage:', error);
      return memoryStorage.get(key) ?? null;
    }
  }
  return memoryStorage.get(key) ?? null;
}

/**
 * Set item in storage (sessionStorage or memory fallback)
 */
function setStorageItem(key: string, value: string): boolean {
  if (isSessionStorageAvailable()) {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      log.warn('Failed to write to sessionStorage, using memory fallback:', error);
      memoryStorage.set(key, value);
      return false;
    }
  }
  memoryStorage.set(key, value);
  return false;
}

/**
 * Remove item from storage (sessionStorage or memory fallback)
 */
function removeStorageItem(key: string): void {
  if (isSessionStorageAvailable()) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      log.warn('Failed to remove from sessionStorage:', error);
    }
  }
  memoryStorage.delete(key);
}

interface SessionData {
  sessionId: string;
  createdAt: number;
  lastActivity: number;
}

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  const randomPart2 = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${randomPart}${randomPart2}`;
}

/**
 * Get the storage key for a workspace
 */
function getStorageKey(workspaceId: string): string {
  return `${SESSION_PREFIX}${workspaceId}`;
}

/**
 * Get session ID for a workspace
 * Returns null if no valid session exists
 * Uses sessionStorage so sessions don't persist across browser sessions
 */
export function getSessionId(workspaceId: string): string | null {
  const key = getStorageKey(workspaceId);
  const stored = getStorageItem(key);

  if (!stored) {
    return null;
  }

  try {
    const data = JSON.parse(stored) as SessionData;
    return data.sessionId;
  } catch (error) {
    log.warn('Failed to parse session data:', error);
    return null;
  }
}

/**
 * Set/update session ID for a workspace
 * Uses sessionStorage so sessions don't persist across browser sessions
 * Returns true if successfully stored in sessionStorage, false if using fallback
 */
export function setSessionId(workspaceId: string, sessionId: string): boolean {
  const key = getStorageKey(workspaceId);
  const existing = getStorageItem(key);

  let data: SessionData;

  if (existing) {
    try {
      const parsed = JSON.parse(existing) as SessionData;
      data = {
        ...parsed,
        sessionId,
        lastActivity: Date.now(),
      };
    } catch {
      data = {
        sessionId,
        createdAt: Date.now(),
        lastActivity: Date.now(),
      };
    }
  } else {
    data = {
      sessionId,
      createdAt: Date.now(),
      lastActivity: Date.now(),
    };
  }

  return setStorageItem(key, JSON.stringify(data));
}

/**
 * Update last activity timestamp for a session
 */
export function updateSessionActivity(workspaceId: string): void {
  const key = getStorageKey(workspaceId);
  const stored = getStorageItem(key);

  if (!stored) {
    return;
  }

  try {
    const data = JSON.parse(stored) as SessionData;
    data.lastActivity = Date.now();
    setStorageItem(key, JSON.stringify(data));
  } catch (error) {
    log.warn('Failed to update session activity:', error);
  }
}

/**
 * Clear session for a workspace
 */
export function clearSession(workspaceId: string): void {
  const key = getStorageKey(workspaceId);
  removeStorageItem(key);
}

/**
 * Get or create a session ID for a workspace
 */
export function getOrCreateSessionId(workspaceId: string): string {
  let sessionId = getSessionId(workspaceId);

  if (!sessionId) {
    sessionId = generateSessionId();
    setSessionId(workspaceId, sessionId);
  } else {
    updateSessionActivity(workspaceId);
  }

  return sessionId;
}

/**
 * Session manager class for more complex session handling
 */
export class SessionManager {
  private workspaceId: string;
  private currentSessionId: string | null = null;

  constructor(workspaceId: string) {
    this.workspaceId = workspaceId;
  }

  /**
   * Get the current session ID, creating one if necessary
   */
  getSession(): string {
    if (!this.currentSessionId) {
      this.currentSessionId = getOrCreateSessionId(this.workspaceId);
    }
    return this.currentSessionId;
  }

  /**
   * Update the session ID (e.g., from server response)
   */
  updateSession(sessionId: string): void {
    this.currentSessionId = sessionId;
    setSessionId(this.workspaceId, sessionId);
  }

  /**
   * Clear the current session
   */
  clearSession(): void {
    this.currentSessionId = null;
    clearSession(this.workspaceId);
  }

  /**
   * Start a new session
   */
  newSession(): string {
    this.clearSession();
    return this.getSession();
  }
}

// =============================================================================
// API Key Storage (using sessionStorage for security)
// =============================================================================

/**
 * Save API key to session storage
 * Uses sessionStorage so key is cleared when browser tab is closed
 * Returns true if successfully stored in sessionStorage, false if using fallback
 */
export function saveApiKey(apiKey: string): boolean {
  return setStorageItem(API_KEY_STORAGE_KEY, apiKey);
}

/**
 * Get API key from session storage
 */
export function getApiKey(): string | null {
  return getStorageItem(API_KEY_STORAGE_KEY);
}

/**
 * Clear API key from session storage
 */
export function clearApiKey(): void {
  removeStorageItem(API_KEY_STORAGE_KEY);
}

/**
 * Check if an API key is stored
 */
export function hasApiKey(): boolean {
  return getApiKey() !== null;
}
